import React, { useState, useEffect } from "react";
import {
  createDiscountRule,
  updateDiscountRule,
  createDiscountRulePortal,
  updateDiscountRulePortal,
} from "../../api/fees/feeApi";
import {
  Modal,
  Button,
  Input,
  Select,
  Textarea,
  Switch,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const RULE_TYPE_OPTIONS = [
  { label: "Sibling", value: "SIBLING" },
  { label: "Merit", value: "MERIT" },
  { label: "Staff", value: "STAFF" },
  { label: "Custom", value: "CUSTOM" },
];

const DISCOUNT_TYPE_OPTIONS = [
  { label: "Flat (₹)", value: "FLAT" },
  { label: "Percentage (%)", value: "PERCENTAGE" },
];

const DiscountRuleModal = ({
  isOpen,
  onClose,
  onSuccess,
  editData = null,
  isPortal = false,
}) => {
  const [form, setForm] = useState({
    name: "",
    description: "",
    ruleType: "CUSTOM",
    discountType: "PERCENTAGE",
    discountValue: 0,
    maxDiscountAmount: "",
    conditionNotes: "",
    priority: 1,
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      setForm({
        name: editData.name || "",
        description: editData.description || "",
        ruleType: editData.ruleType || "CUSTOM",
        discountType: editData.discountType || "PERCENTAGE",
        discountValue: editData.discountValue ?? 0,
        maxDiscountAmount: editData.maxDiscountAmount ?? "",
        conditionNotes: editData.conditionNotes || "",
        priority: editData.priority ?? 1,
        isActive: editData.isActive !== false,
      });
    } else {
      setForm({
        name: "",
        description: "",
        ruleType: "CUSTOM",
        discountType: "PERCENTAGE",
        discountValue: 0,
        maxDiscountAmount: "",
        conditionNotes: "",
        priority: 1,
        isActive: true,
      });
    }
  }, [isOpen, editData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSelectChange = (name, val) => {
    setForm((p) => ({ ...p, [name]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        discountValue: Number(form.discountValue),
        priority: Number(form.priority),
        maxDiscountAmount:
          form.maxDiscountAmount === "" ? null : Number(form.maxDiscountAmount),
      };
      if (editData?._id) {
        if (isPortal) await updateDiscountRulePortal(editData._id, payload);
        else await updateDiscountRule(editData._id, payload);
      } else {
        if (isPortal) await createDiscountRulePortal(payload);
        else await createDiscountRule(payload);
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editData ? "Edit Discount Rule" : "Add Discount Rule"}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="discount-rule-form"
            variant="primary"
            isLoading={loading}
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="discount-rule-form"
        onSubmit={handleSubmit}
        className="space-y-4 pt-1"
      >
        

        <Input
          label="Name *"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="e.g. Sibling Discount, Merit Scholarship"
          required
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Rule Type"
            options={RULE_TYPE_OPTIONS}
            value={form.ruleType}
            onChange={(val) => handleSelectChange("ruleType", val)}
          />

          <Select
            label="Discount Type"
            options={DISCOUNT_TYPE_OPTIONS}
            value={form.discountType}
            onChange={(val) => handleSelectChange("discountType", val)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Value"
            type="number"
            min="0"
            step="0.01"
            name="discountValue"
            value={form.discountValue}
            onChange={handleChange}
          />

          <Input
            label="Max Cap (optional)"
            type="number"
            min="0"
            step="0.01"
            name="maxDiscountAmount"
            value={form.maxDiscountAmount}
            onChange={handleChange}
            placeholder="No upper limit"
          />
        </div>

        <Textarea
          label="Condition notes"
          name="conditionNotes"
          value={form.conditionNotes}
          onChange={handleChange}
          rows={3}
          placeholder="Enter terms, eligibility criteria, or internal notes..."
        />

        <div className="pt-1">
          <Switch
            label="Active"
            checked={form.isActive}
            onChange={(checked) =>
              setForm((p) => ({ ...p, isActive: checked }))
            }
          />
        </div>
      </form>
    </Modal>
  );
};

export default DiscountRuleModal;